import React, { useEffect } from 'react'
import { useHookstate } from '@hookstate/core'
import {
  IconButton,
  InputAdornment,
  OutlinedInput,
  Button,
  Card,
  CardHeader,
  Collapse,
  Grid,
  FormHelperText,
  FormControl,
  Box,
} from '@mui/material'
import { useLang } from '~/hooks/useLang'
import CancelIcon from '@mui/icons-material/Cancel'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from '~/components/ErrorFallback'
import Layout from '~/components/Layout'
import Loading from '~/components/Loading'
import notify from '~/utils/notify'
import { authFetch } from '~/services/fetch'
import useReportState from '../../stores/ReportState'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterSwitch from './SpecificFilters/FilterSwitchGroupLocation'
import {
  downloadReportCSV,
  makeRequestToGetReportSearchMultipleComents,
} from '../../services/report'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies'
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups'
import { LoadingButton } from '@mui/lab'
import GenerateReportButton from '../../components/GenerateReportButton'
import FilterSwitchOtherDispatchsGroup from '~/components/FilterSwitchOtherDispatchsGroups'
import FilterKeyword from '../../components/FilterKeyword'
import FilterAutocompleteCity from '~/components/FilterSelectAutocompleteCity'
import FilterAutocompleteEntryOrigin from '~/components/FilterAutocompleteEntryOrigin'
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet'
import FilterAutocompleteOperation from '~/components/FilterAutocompleteOperation'
import FilterTextField from '~/components/FilterTextField'
import ExportButtonCSV from '../../components/ButtonExportCSV'
import FilterAutocompleteDistrict from '~/components/FilterSelectAutocompleteDistrict'
import { generateCSVFileName } from '~/utils/strings'

// eslint-disable-next-line max-lines-per-function
export default function SearchMultipleComments() {
  const { reportData, loadingReport } = useReportState()
  const { translate } = useLang()
  const comments = useHookstate('')
  const result = useHookstate([''])
  const expandFilterSearch = useHookstate<boolean>(true)
  const includeAllWords = useHookstate<boolean>(false)
  const lockFields = useHookstate<boolean>(false)
  const isExportingCSV = useHookstate<boolean>(false)
  const isGeneratingReport = useHookstate<boolean>(false)
  const startDate = useHookstate<string | null>(null)
  const finishDate = useHookstate<string | null>(null)
  const agencyIds = useHookstate<readonly number[]>([])
  const deviceId = useHookstate<number | null>(null)
  const groupId = useHookstate<readonly number[]>([])
  const labelButton = useHookstate<string | null>(null)
  const withSolvedThirdParty = useHookstate<boolean>(false)
  const cityId = useHookstate<number | null>(null)
  const entryOriginId = useHookstate<number | null>(null)
  const groupSetId = useHookstate<number | null>(null)
  const period = useHookstate('')
  const operationId = useHookstate<number | null>(null)
  const location = useHookstate<string | null>(null)
  const districtId = useHookstate<number | null>(null)

  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  const makeCommentsRequest = () => {
    result.set(comments.get().split(';'))
  }

  async function generateReport(exportCSV = false) {
    makeCommentsRequest()
    if (comments.value && startDate && finishDate) {
      loadingReport().set(true)
      if (exportCSV) {
        isExportingCSV.set(true)
      } else {
        isGeneratingReport.set(true)
      }
      const data = await makeRequestToGetReportSearchMultipleComents({
        keyWords: result.get(),
        includeAllWords: includeAllWords.get(),
        startDate: startDate.get(),
        finishDate: finishDate.get(),
        dispatchGroupsId: groupId.get(),
        agencyIds: agencyIds.get(),
        withSolvedThirdParty: withSolvedThirdParty.get(),
        cityId: cityId.get(),
        entryOriginId: entryOriginId.get(),
        groupSetId: groupSetId.get(),
        operationId: operationId.get(),
        location: location.get(),
        districtId: districtId.get(),

        exportCSV: exportCSV,
      }).finally(() => {
        loadingReport().set(false)
        if (exportCSV) {
          isExportingCSV.set(false)
        } else {
          isGeneratingReport.set(false)
        }
      })
      if (!data || (Array.isArray(data) && data.length === 0)) {
        notify({
          message: translate('Não há dados suficientes para gerar o relatório'),
          type: 'error',
        })
        return
      }

      if (exportCSV) {
        downloadReportCSV(
          generateCSVFileName('Search multiple coments', translate),
          data
        )
      } else {
        setTimeout(() => {
          reportData().set(data)
        })
        window.open(`/report/result/search-multiple-coments`)
      }
    } else {
      notify({ message: translate('Keyword is mandatory'), type: 'error' })
    }
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true}>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader
                title={`${translate('Report')}: ${translate(
                  `Search multiple coments`
                )}`}
              />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader
            title={`${translate('Filters')}`}
            avatar={
              <IconButton
                onClick={() =>
                  expandFilterSearch.set(!expandFilterSearch.get())
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginRight: 1,
                  padding: 0,
                }}
              >
                {expandFilterSearch.value ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            }
          />
          <Collapse in={expandFilterSearch.get()}>
            <Grid
              container
              xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid container direction={'column'} rowGap={2}>
                <Grid container direction={'column'} rowGap={2} xs={12}>
                  <Grid item xs={5} sx={{ pl: 1, pt: 1 }}>
                    <FilterKeyword keyWords={comments} />
                    {comments.get().trim() !== '' && (
                      <Box sx={{ paddingTop: 1, paddingLeft: 1 }}>
                        <FilterSwitch
                          label={translate('Contain all keywords')}
                          onCheckedChange={() => {
                            includeAllWords?.set(!includeAllWords.value)
                          }}
                          checked={includeAllWords?.get()}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid container direction={'row'} xs={12}>
                    <FilterSelectPeriod
                      disabled={lockFields.get()}
                      onStartDateChange={(value) =>
                        startDate.set(value?.toJSON() ?? null)
                      }
                      onFinishDateChange={(value) =>
                        finishDate.set(value?.toJSON() ?? null)
                      }
                      onPeriodChange={(value) => {
                        period.set(value)
                      }}
                      period={period.get()}
                      required
                    />
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteAgencies
                        disabled={lockFields.get()}
                        agenciesId={agencyIds.get()}
                        onAgenciesChange={(agency) => {
                          labelButton.set(translate('Search'))
                          agencyIds.set(agency)
                        }}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteDispatchGroupSet
                        disabled={lockFields.get()}
                        dispatchGroupSetId={groupSetId?.get() ?? undefined}
                        onGroupDispatchSetChange={(group) => {
                          groupSetId?.set(group)
                        }}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteDispatchsGroups
                        disabled={lockFields.get()}
                        agencyId={agencyIds.get() ?? undefined}
                        dispatcheGroups={groupId.get() ?? undefined}
                        onGroupDispatchChange={(group) => {
                          groupId.set(group)
                          deviceId.set(null)
                          labelButton.set(translate('Search'))
                        }}
                        groupSetId={groupSetId.get()}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteCity
                        disabled={lockFields.get()}
                        onCityChange={(city) => {
                          cityId?.set(city)
                          labelButton.set(translate('Search'))
                        }}
                        cityId={cityId?.get()}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteDistrict
                        disabled={!cityId?.get()}
                        onDistrictChange={(district) => {
                          districtId?.set(district)
                        }}
                        districtId={districtId}
                        cityId={cityId ? cityId.get() : null}
                      />
                    </Grid>
                    <Grid item xs={4} sx={{ pl: 1, pt: 1 }}>
                      <FilterTextField
                        currentText={location.get()}
                        label={'Location'}
                        onTextFieldChange={(textField) => {
                          location.set(textField)
                        }}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteEntryOrigin
                        disabled={lockFields.get()}
                        onEntryOriginChange={(entryOrigin) => {
                          entryOriginId?.set(entryOrigin)
                          labelButton.set(translate('Search'))
                        }}
                        originId={entryOriginId?.get()}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteOperation
                        disabled={lockFields.get()}
                        onOperationChange={(operation) => {
                          operationId.set(operation)
                          labelButton.set(translate('Search'))
                        }}
                        operationId={operationId.get()}
                      />
                    </Grid>
                  </Grid>

                  <Grid container direction={'row'} xs={12}>

                    <Grid item xs={4} sx={{ pl: 1, pt: 1 }}>
                      <FilterSwitchOtherDispatchsGroup
                        onCheckedChange={() => {
                          labelButton.set(translate('Search'))
                          withSolvedThirdParty.set(!withSolvedThirdParty.value)
                        }}
                        disabled={lockFields.get()}
                        checked={withSolvedThirdParty.get()}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      comments.set('')
                      period.set('')
                      deviceId.set(null)
                      agencyIds.set([])
                      groupSetId.set(null)
                      groupId.set([])
                      entryOriginId.set(null)
                      cityId.set(null)
                      location.set(null)
                      withSolvedThirdParty.set(false)
                      includeAllWords.set(false)
                      operationId.set(null)
                      districtId.set(null)
                    }}
                    sx={{ height: '40px' }}
                  >
                    {translate('Clear Filters')}
                  </Button>
                  <ExportButtonCSV
                    isLoading={isExportingCSV.get()}
                    dataToExport={() => generateReport(true)}
                  />
                  <GenerateReportButton
                    isLoading={isGeneratingReport.get()}
                    makeReport={generateReport}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary>
  )
}
